// Telemedicine workspace (proposal Section 07): the doctor joins with the chart already half-written —
// kiosk vitals, Urdu intake (coded) and the decision-support reasoning arrive before the call connects.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VITALS, type VitalCode } from '@shared';
import { ApiError, staff, type StaffUser } from '../lib/api.ts';
import type { Realtime, ServerMessage } from '../lib/realtime.ts';
import { ConsultCall, type CallMode } from '../lib/rtc.ts';
import { ageFrom, ago, Login, StaffHeader, useLive, useStaffSession } from '../ui/staff.tsx';

interface QueueItem {
  id: string;
  encounter_id: string;
  status: 'waiting' | 'active' | 'closed';
  priority: 'urgent' | 'review' | 'routine';
  mode: string;
  requested_at: string;
  clinician_id: string | null;
  clinician: string | null;
  facility: string;
  district: string;
  sex: string | null;
  birth_year: number | null;
  patient_name: string | null;
  reasons: string[];
  symptoms: string[];
}

interface Detail {
  id: string;
  facility: string;
  facility_type: string;
  district: string;
  language: string;
  session_mode: string;
  visit_code: string | null;
  priority: string | null;
  route: string | null;
  triage: { reasons: string[]; flags: { rule: string; severity: string; en: string; value?: number; vital?: VitalCode }[]; thresholdVersion: string } | null;
  intake: { text: string; language: string; durationDays?: number; findings: { id: string; en: string; ur: string; snomed: string; icd10: string; negated: boolean; redFlag: boolean }[] } | null;
  vitals: Partial<Record<VitalCode, number>>;
  patient: { id: string; cnic_masked: string | null; name: string | null; sex: string; birth_year: number | null; whatsapp_opt_in: number; guest: number } | null;
  observations: { code: VitalCode; value: number; quality: number; attempt: number }[];
  flags: { id: string; rule: string; severity: string; message: string; status: string }[];
  history: { id: string; created_at: string; priority: string | null; facility: string; bp_sys: number | null; bp_dia: number | null; spo2: number | null }[];
  consult: { id: string; status: string; clinician_id: string | null; mode: string; ai_route: string | null } | null;
}

interface Drug { id: string; name: string; form: string }
interface RxRow { drugId: string; dose: string; frequency: string; days: number; instructions?: string }
interface Warning { level: 'block' | 'caution'; message: string }

export function ClinicianApp() {
  const session = useStaffSession();
  if (session.checking) return <div className="center muted">Loading…</div>;
  if (!session.user) return <Login surface="clinician" title="Telemedicine workspace" onLogin={session.setUser} />;
  if (session.user.role !== 'clinician') {
    return (
      <div className="center">
        <p>The telemedicine workspace is for clinician accounts. You are signed in as <strong>{session.user.role}</strong>.</p>
        <button className="btn" onClick={session.logout}>Sign out</button>
      </div>
    );
  }
  return <Workspace user={session.user} rt={session.rt!} onLogout={session.logout} />;
}

function Workspace({ user, rt, onLogout }: { user: StaffUser; rt: Realtime; onLogout: () => void }) {
  const live = useLive(rt);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    try {
      setQueue(await staff.get<QueueItem[]>('/api/consults?status=waiting,active'));
    } catch {
      /* transient */
    }
  }, []);

  useEffect(() => {
    void load();
    const off = rt.on((m) => {
      if (m.type === 'event' && m.event.startsWith('consult.')) void load();
    });
    const iv = setInterval(() => { void load(); tick((x) => x + 1); }, 20000);
    return () => {
      off();
      clearInterval(iv);
    };
  }, [load, rt]);

  const mine = queue.filter((q) => q.status === 'active' && q.clinician_id === user.id);
  const waiting = queue.filter((q) => q.status === 'waiting');
  const others = queue.filter((q) => q.status === 'active' && q.clinician_id !== user.id);

  return (
    <div className="staff clinician">
      <StaffHeader title="Telemedicine workspace" user={user} onLogout={onLogout} live={live} />
      <div className="cl-layout">
        <aside className="cl-queue">
          <QueueSection title="My active consults" items={mine} selected={selected} onSelect={setSelected} />
          <QueueSection title={`Waiting (${waiting.length})`} items={waiting} selected={selected} onSelect={setSelected} />
          {others.length > 0 && <QueueSection title="With other clinicians" items={others} selected={selected} onSelect={setSelected} />}
          {queue.length === 0 && <p className="muted pad">No patients waiting. New kiosk referrals appear here live.</p>}
        </aside>
        <main className="cl-main">
          {selected && queue.some((q) => q.id === selected) ? <ConsultWorkspace key={selected} consultId={selected} queueItem={queue.find((q) => q.id === selected)} user={user} rt={rt} onChanged={load} onClosed={() => { setSelected(null); void load(); }} />
            : <div className="empty">Select a patient from the queue. Urgent patients are listed first.</div>}
        </main>
      </div>
    </div>
  );
}

function QueueSection({ title, items, selected, onSelect }: { title: string; items: QueueItem[]; selected: string | null; onSelect: (id: string) => void }) {
  if (!items.length) return null;
  return (
    <section>
      <h3 className="q-title">{title}</h3>
      <ul className="q-list">
        {items.map((q) => (
          <li key={q.id}>
            <button className={`q-item ${selected === q.id ? 'on' : ''}`} onClick={() => onSelect(q.id)}>
              <span className={`prio prio-${q.priority}`}>{q.priority === 'urgent' ? '▲ Urgent' : q.priority === 'review' ? '● Review' : '○ Routine'}</span>
              <span className="q-who">{q.patient_name ?? 'Patient'} · {q.sex ?? '—'} · {ageFrom(q.birth_year)}</span>
              <span className="q-where muted small">{q.facility} · {q.district}</span>
              <span className="q-why small">{q.reasons[0]?.replace(/^(URGENT|REVIEW) /, '') ?? ''}{q.symptoms.length ? ` · ${q.symptoms.join(', ')}` : ''}</span>
              <span className="q-time muted small">{ago(q.requested_at)} · {q.mode}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const ROUTES = [
  { id: 'home_care', label: 'Home care / advice' },
  { id: 'queue', label: 'See facility staff (routine)' },
  { id: 'teleconsult', label: 'Further teleconsult' },
  { id: 'urgent_review', label: 'Urgent in-person review' },
  { id: 'referral', label: 'Refer to THQ/DHQ' },
];

function ConsultWorkspace({ consultId, queueItem, user, rt, onChanged, onClosed }: {
  consultId: string; queueItem?: QueueItem; user: StaffUser; rt: Realtime; onChanged: () => void; onClosed: () => void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState('');
  const [formulary, setFormulary] = useState<Drug[]>([]);
  const encounterId = useRef(queueItem?.encounter_id);

  const load = useCallback(async () => {
    try {
      setDetail(await staff.get<Detail>(`/api/encounters/${encounterId.current}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    void staff.get<Drug[]>('/api/formulary').then(setFormulary);
  }, [load]);

  if (error) return <div className="error pad">{error}</div>;
  if (!detail) return <div className="muted pad">Loading patient record…</div>;
  const c = detail.consult!;
  const mineActive = c.status === 'active' && c.clinician_id === user.id;

  return (
    <div className="cw">
      <PatientBanner d={detail} q={queueItem} />
      <div className="cw-grid">
        <div className="cw-col">
          <VitalsPanel d={detail} />
          <DecisionSupport d={detail} />
          <IntakePanel d={detail} />
          <HistoryPanel d={detail} />
        </div>
        <div className="cw-col">
          {c.status === 'waiting' && (
            <div className="panel join-panel">
              <p>{c.mode === 'async' ? 'Store-and-forward request: the patient may have left the kiosk. Review and record advice; follow-up is sent by WhatsApp when the patient opted in.' : 'The patient is waiting at the kiosk.'}</p>
              <button className="btn btn-primary" onClick={async () => {
                try {
                  await staff.post(`/api/consults/${consultId}/claim`);
                  await load();
                  onChanged();
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                }
              }}>{c.mode === 'async' ? 'Start review' : 'Join consultation'}</button>
            </div>
          )}
          {c.status === 'active' && !mineActive && <div className="panel notice">This consult is with another clinician.</div>}
          {mineActive && c.mode !== 'async' && <CallPanel consultId={consultId} rt={rt} initialMode={c.mode as CallMode} />}
          {mineActive && <ClosePanel d={detail} consultId={consultId} formulary={formulary} onClosed={onClosed} />}
        </div>
      </div>
    </div>
  );
}

function PatientBanner({ d, q }: { d: Detail; q?: QueueItem }) {
  const p = d.patient;
  return (
    <div className="banner">
      <div>
        <div className="banner-name">{p?.name ?? (p?.guest ? 'Guest patient' : 'Patient')}</div>
        <div className="muted small">
          {p?.sex ?? '—'} · {ageFrom(p?.birth_year)} · CNIC {p?.cnic_masked ?? 'not provided'} · {p?.whatsapp_opt_in ? 'WhatsApp follow-up ✓' : 'no WhatsApp'}
        </div>
      </div>
      <div className="banner-right">
        {d.priority && <span className={`prio prio-${d.priority}`}>{d.priority === 'urgent' ? '▲ Urgent' : d.priority === 'review' ? '● Review' : '○ Routine'}</span>}
        <div className="muted small">{d.facility} ({d.facility_type}) · {d.district} · visit {d.visit_code ?? '—'} · {d.session_mode === 'assisted' ? 'staff-assisted' : 'self-service'} · {d.language === 'ur' ? 'Urdu' : 'English'}</div>
        {q && <div className="muted small">Requested {ago(q.requested_at)}</div>}
      </div>
    </div>
  );
}

function VitalsPanel({ d }: { d: Detail }) {
  const flagged = new Map<VitalCode, NonNullable<Detail['triage']>['flags'][number]>(d.triage?.flags.filter((f) => f.vital).map((f) => [f.vital === 'bp_dia' ? 'bp_sys' : f.vital!, f]) ?? []);
  const show: VitalCode[] = ['bp_sys', 'pulse', 'spo2', 'temp', 'height', 'weight', 'bmi'];
  const retries = d.observations.filter((o) => o.attempt > 1).length;
  return (
    <section className="panel">
      <h3>Kiosk vitals</h3>
      <div className="vitals-grid">
        {show.map((code) => {
          const v = d.vitals[code];
          const f = flagged.get(code);
          return (
            <div key={code} className={`vital-tile ${f ? `sev-${f.severity}` : ''}`}>
              <div className="muted small">{code === 'bp_sys' ? 'Blood pressure' : VITALS[code].display}</div>
              <div className="vital-value">{v === undefined ? '—' : code === 'bp_sys' ? `${v}/${d.vitals.bp_dia ?? '—'}` : v} <small>{v === undefined ? '' : VITALS[code].unitLabel}</small></div>
              {f && <div className="small">{f.severity === 'urgent' ? '▲' : '●'} {f.en}</div>}
            </div>
          );
        })}
      </div>
      <p className="muted small">Readings passed the kiosk quality gate{retries ? ` (${retries} repeated for poor signal)` : ''}. Thresholds: {d.triage?.thresholdVersion ?? '—'}.</p>
    </section>
  );
}

function DecisionSupport({ d }: { d: Detail }) {
  if (!d.triage) return null;
  return (
    <section className="panel">
      <h3>Decision support <span className="muted small">— suggestion only; you decide</span></h3>
      <p>Suggested route: <strong>{d.route?.replace('_', ' ')}</strong></p>
      <ul className="reasons">{d.triage.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      {d.flags.length > 0 && <p className="small muted">{d.flags.filter((f) => f.status === 'open').length} open flag(s) will be closed with this consult as their disposition.</p>}
    </section>
  );
}

function IntakePanel({ d }: { d: Detail }) {
  if (!d.intake) return <section className="panel"><h3>Patient-reported symptoms</h3><p className="muted">None reported at the kiosk.</p></section>;
  return (
    <section className="panel">
      <h3>Patient-reported symptoms</h3>
      <blockquote dir="auto" lang={d.intake.language === 'ur' ? 'ur' : undefined} className="intake-quote">{d.intake.text}</blockquote>
      <table className="table small">
        <thead><tr><th>Finding</th><th>SNOMED CT</th><th>ICD-10</th><th></th></tr></thead>
        <tbody>
          {d.intake.findings.map((f) => (
            <tr key={f.id}><td>{f.negated ? <s>{f.en}</s> : f.en}{f.redFlag && !f.negated ? ' ▲' : ''}</td><td>{f.snomed}</td><td>{f.icd10}</td><td className="muted">{f.negated ? 'denied' : ''}</td></tr>
          ))}
        </tbody>
      </table>
      {d.intake.durationDays ? <p className="small">Duration: about {d.intake.durationDays} day(s)</p> : null}
    </section>
  );
}

function HistoryPanel({ d }: { d: Detail }) {
  if (!d.history.length) return null;
  return (
    <section className="panel">
      <h3>Previous kiosk visits</h3>
      <table className="table small">
        <thead><tr><th>Date</th><th>Facility</th><th>BP</th><th>SpO₂</th><th>Priority</th></tr></thead>
        <tbody>{d.history.map((h) => <tr key={h.id}><td>{h.created_at.slice(0, 10)}</td><td>{h.facility}</td><td>{h.bp_sys ? `${h.bp_sys}/${h.bp_dia}` : '—'}</td><td>{h.spo2 ?? '—'}</td><td>{h.priority ?? '—'}</td></tr>)}</tbody>
      </table>
    </section>
  );
}

function CallPanel({ consultId, rt, initialMode }: { consultId: string; rt: Realtime; initialMode: CallMode }) {
  const [mode, setMode] = useState<CallMode>(initialMode === 'audio' ? 'audio' : 'video');
  const [state, setState] = useState('connecting');
  const [messages, setMessages] = useState<{ sender: string; body: string }[]>([]);
  const [draft, setDraft] = useState('');
  const [peer, setPeer] = useState(false);
  const remote = useRef<HTMLVideoElement>(null);
  const local = useRef<HTMLVideoElement>(null);
  const call = useRef<ConsultCall | null>(null);

  useEffect(() => {
    const c = new ConsultCall(rt, consultId, false);
    c.onRemote = (s) => { if (remote.current) remote.current.srcObject = s; };
    c.onLocal = (s) => { if (local.current) local.current.srcObject = s; };
    c.onState = setState;
    c.onModeRequest = (m) => { setMode(m); void c.start(m); };
    call.current = c;
    const off = rt.on((m: ServerMessage) => {
      if (m.type === 'joined' && m.consultId === consultId) setPeer(m.peers > 0);
      if (m.type === 'peer-joined' && m.consultId === consultId) setPeer(true);
      if (m.type === 'peer-left' && m.consultId === consultId) setPeer(false);
      if (m.type === 'chat' && m.consultId === consultId) setMessages((x) => [...x, { sender: m.sender, body: m.body }]);
    });
    rt.join(consultId);
    void staff.get<{ sender: string; body: string }[]>(`/api/consults/${consultId}/messages`).then(setMessages).catch(() => {});
    void c.start(mode);
    return () => {
      off();
      c.end();
      rt.leave(consultId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultId, rt]);

  const switchMode = (m: CallMode) => {
    setMode(m);
    call.current?.requestMode(m);
    void call.current?.start(m);
  };

  return (
    <section className="panel call">
      <h3>Consultation <span className="muted small">· {peer ? 'kiosk connected' : 'waiting for kiosk'} · {state}</span></h3>
      {mode !== 'chat' && (
        <div className="video-wrap small-video">
          <video ref={remote} autoPlay playsInline className="video-remote" />
          <video ref={local} autoPlay playsInline muted className="video-local" />
        </div>
      )}
      <div className="row">
        {(['video', 'audio', 'chat'] as CallMode[]).map((m) => <button key={m} className={`btn btn-small ${mode === m ? 'btn-primary' : ''}`} onClick={() => switchMode(m)}>{m}</button>)}
        <span className="muted small">Weak link? Drop to audio or chat.</span>
      </div>
      <div className="chat">
        <div className="chat-log">{messages.map((m, i) => <div key={i} className={`msg ${m.sender === 'patient' ? '' : 'me'}`}>{m.body}</div>)}</div>
        <form className="row" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) { rt.send({ type: 'chat', consultId, body: draft }); setDraft(''); } }}>
          <input className="input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message the patient (Urdu or English)" />
          <button className="btn btn-primary">Send</button>
        </form>
      </div>
    </section>
  );
}

function ClosePanel({ d, consultId, formulary, onClosed }: { d: Detail; consultId: string; formulary: Drug[]; onClosed: () => void }) {
  const [summary, setSummary] = useState('');
  const [route, setRoute] = useState(d.route === 'urgent_review' ? 'urgent_review' : 'home_care');
  const [overrideReason, setOverrideReason] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [allergies, setAllergies] = useState('');
  const [rx, setRx] = useState<RxRow[]>([]);
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [ack, setAck] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const aiRoute = d.consult?.ai_route ?? d.route;
  const needsReason = Boolean(aiRoute && aiRoute !== route && !(aiRoute === 'teleconsult' && ['queue', 'home_care'].includes(route)));
  const allergyList = useMemo(() => allergies.split(',').map((a) => a.trim()).filter(Boolean), [allergies]);

  useEffect(() => {
    const id = setTimeout(async () => {
      if (!rx.length) return setWarnings([]);
      try {
        const r = await staff.post<{ warnings: Warning[] }>(`/api/consults/${consultId}/rx-check`, { items: rx, allergies: allergyList });
        setWarnings(r.warnings);
        setAck(false);
      } catch {
        /* checked again on close */
      }
    }, 300);
    return () => clearTimeout(id);
  }, [rx, allergyList, consultId]);

  const blocking = warnings.some((w) => w.level === 'block');
  const close = async () => {
    setBusy(true);
    setError('');
    try {
      await staff.post(`/api/consults/${consultId}/close`, {
        summary, route, overrideReason: needsReason ? overrideReason : undefined,
        followUpDays: followUp ? Number(followUp) : undefined, allergies: allergyList, prescription: rx, acknowledgeWarnings: ack,
      });
      onClosed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      if (e instanceof ApiError && Array.isArray(e.details)) setWarnings(e.details as Warning[]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h3>Close consultation</h3>
      <label>Clinical summary &amp; advice (shown to the patient)
        <textarea className="input textarea" rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Assessment, advice in plain language…" />
      </label>
      <div className="row wrap">
        <label>Disposition
          <select className="input" value={route} onChange={(e) => setRoute(e.target.value)}>
            {ROUTES.map((r) => <option key={r.id} value={r.id}>{r.label}{r.id === aiRoute ? ' (suggested)' : ''}</option>)}
          </select>
        </label>
        <label>Follow-up (days)
          <input className="input" inputMode="numeric" value={followUp} onChange={(e) => setFollowUp(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 7" />
        </label>
      </div>
      {needsReason && (
        <label className="override">Reason for differing from decision support (required, audited)
          <input className="input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
        </label>
      )}
      <h4>e-Prescription</h4>
      <label>Known allergies (comma separated)
        <input className="input" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="e.g. penicillin" />
      </label>
      {rx.map((r, i) => (
        <div key={i} className="rx-row">
          <select className="input" value={r.drugId} onChange={(e) => setRx(rx.map((x, j) => (j === i ? { ...x, drugId: e.target.value } : x)))}>
            {formulary.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.form})</option>)}
          </select>
          <input className="input" value={r.dose} onChange={(e) => setRx(rx.map((x, j) => (j === i ? { ...x, dose: e.target.value } : x)))} placeholder="dose" />
          <input className="input" value={r.frequency} onChange={(e) => setRx(rx.map((x, j) => (j === i ? { ...x, frequency: e.target.value } : x)))} placeholder="frequency" />
          <input className="input narrow" inputMode="numeric" value={r.days} onChange={(e) => setRx(rx.map((x, j) => (j === i ? { ...x, days: Number(e.target.value.replace(/\D/g, '')) || 0 } : x)))} />
          <span className="muted small">days</span>
          <button className="btn btn-small" onClick={() => setRx(rx.filter((_, j) => j !== i))} aria-label="remove">✕</button>
        </div>
      ))}
      <button className="btn btn-small" onClick={() => setRx([...rx, { drugId: formulary[0]?.id ?? 'paracetamol-500', dose: '1 tablet', frequency: 'three times a day', days: 3 }])}>+ Add medicine</button>
      {warnings.length > 0 && (
        <ul className="warnings">
          {warnings.map((w) => <li key={w.message} className={w.level}>{w.level === 'block' ? '⛔ ' : '⚠ '}{w.message}</li>)}
        </ul>
      )}
      {warnings.length > 0 && !blocking && (
        <label className="check"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I have reviewed these warnings</label>
      )}
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button className="btn btn-primary" disabled={busy || summary.trim().length < 3 || blocking || (warnings.length > 0 && !ack) || (needsReason && overrideReason.trim().length < 3)} onClick={close}>
          Close &amp; send to kiosk
        </button>
      </div>
      <p className="muted small">Prescribing responsibility remains with the clinician and the Department (proposal Section 14).</p>
    </section>
  );
}
